type TimeZoneOffsetParts = {
  hours: number;
  minutes: number;
  sign: '+' | '-';
};

const dateTimeInTimeZoneFormatters = new Map<string, Intl.DateTimeFormat>();
const localDateTimePattern = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?$/;
const offsetSuffixPattern = /(Z|[+-]\d{2}:?\d{2})$/;
const timeZoneOffsetFormatters = new Map<string, Intl.DateTimeFormat>();
const dateOnlyFormatters = new Map<string, Intl.DateTimeFormat>();
export const dateTimeMinTimeValue = new Date(-8640000000000000).getTime();

const getDateOnlyFormatter = (timeZone: string, month: Intl.DateTimeFormatOptions['month'] = '2-digit'): Intl.DateTimeFormat => {
  const cacheKey = `${timeZone}:${month}`;
  let formatter = dateOnlyFormatters.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      day: month === '2-digit' ? '2-digit' : 'numeric',
      month,
      timeZone,
      year: 'numeric',
    });
    dateOnlyFormatters.set(cacheKey, formatter);
  }
  return formatter;
};

const getDateTimeInTimeZoneFormatter = (timeZone: string): Intl.DateTimeFormat => {
  let formatter = dateTimeInTimeZoneFormatters.get(timeZone);

  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      day: '2-digit',
      hour: '2-digit',
      hourCycle: 'h23',
      minute: '2-digit',
      month: '2-digit',
      second: '2-digit',
      timeZone,
      year: 'numeric',
    });
    dateTimeInTimeZoneFormatters.set(timeZone, formatter);
  }

  return formatter;
};

const getFractionalMilliseconds = (fractionalSeconds: string | undefined): number => {
  if (!fractionalSeconds) {
    return 0;
  }

  return parseInt(fractionalSeconds.padEnd(3, '0').slice(0, 3), 10);
};

const getTimeZoneOffsetFormatter = (timeZone: string): Intl.DateTimeFormat => {
  let formatter = timeZoneOffsetFormatters.get(timeZone);

  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'longOffset',
    });
    timeZoneOffsetFormatters.set(timeZone, formatter);
  }

  return formatter;
};

const getTimeZoneOffsetParts = (date: Date, timeZone: string): TimeZoneOffsetParts | null => {
  const parts = getTimeZoneOffsetFormatter(timeZone).formatToParts(date);
  const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value ?? '';
  const offsetMatch = timeZoneName.match(/GMT([+-]\d{1,2})(?::(\d{2}))?/);

  if (!offsetMatch) {
    return null;
  }

  const hours = Math.abs(parseInt(offsetMatch[1], 10));
  const minutes = parseInt(offsetMatch[2] ?? '0', 10);
  const sign = offsetMatch[1].startsWith('-') ? '-' : '+';

  return {
    hours,
    minutes,
    sign,
  };
};

const getTimeZoneOffsetMs = (date: Date, timeZone: string): number => {
  const offsetParts = getTimeZoneOffsetParts(date, timeZone);

  if (!offsetParts) {
    return 0;
  }

  const multiplier = offsetParts.sign === '-' ? -1 : 1;

  return multiplier * (offsetParts.hours * 60 + offsetParts.minutes) * 60 * 1000;
};

export const formatDateTimeWithoutOffset = (date: Date, timeZone: string | undefined): string => {
  const resolvedTimeZone = timeZone || 'America/Chicago';
  const formatter = getDateTimeInTimeZoneFormatter(resolvedTimeZone);
  const parts = formatter.formatToParts(date);
  const getPart = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find(part => part.type === type)?.value ?? '00';

  return `${getPart('year')}-${getPart('month')}-${getPart('day')}T${getPart('hour')}:${getPart('minute')}:${getPart('second')}`;
};

export const formatDateTimeWithTimezone = (date: Date, timeZone: string | undefined): string => {
  const resolvedTimeZone = timeZone || 'America/Chicago';
  const dateStr = formatDateTimeWithoutOffset(date, resolvedTimeZone);
  const offsetParts = getTimeZoneOffsetParts(date, resolvedTimeZone);

  if (!offsetParts) {
    return `${dateStr}Z`;
  }

  return `${dateStr}${offsetParts.sign}${offsetParts.hours.toString().padStart(2, '0')}:${offsetParts.minutes.toString().padStart(2, '0')}`;
};

export const formatDateTimeForAnalytics = (date?: Date): string => {
  if (!date) return '';

  const hours = date.getUTCHours();
  const hour = hours % 12 || 12;
  const meridiem = hours >= 12 ? 'PM' : 'AM';
  const datePart = `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
  const timePart = `${hour}:${date.getUTCMinutes().toString().padStart(2, '0')}:${date.getUTCSeconds().toString().padStart(2, '0')}`;

  return `${datePart} ${timePart} ${meridiem}`;
};

export const formatDateWithTimezoneLong = (date?: Date, timeZone?: string): string => {
  if (!date) return '';

  const resolvedTimeZone = timeZone || 'America/Chicago';

  return getDateOnlyFormatter(resolvedTimeZone, 'long').format(date);
};

export const formatDateWithTimezoneShort = (date?: Date, timeZone?: string): string => {
  if (!date) return '';

  const resolvedTimeZone = timeZone || 'America/Chicago';

  return getDateOnlyFormatter(resolvedTimeZone, 'short').format(date);
};

export const normalizeDateTimeForTimeZone = (value: Date | string, timeZone?: string): Date => {
  if (value instanceof Date) {
    return new Date(value);
  }

  if (!timeZone || offsetSuffixPattern.test(value)) {
    return new Date(value);
  }

  const parts = value.match(localDateTimePattern);

  if (!parts) {
    return new Date(value);
  }

  const [, year, month, day, hour, minute, second = '00', fractionalSeconds] = parts;
  const baseUtcMs = Date.UTC(
    parseInt(year, 10),
    parseInt(month, 10) - 1,
    parseInt(day, 10),
    parseInt(hour, 10),
    parseInt(minute, 10),
    parseInt(second, 10),
    getFractionalMilliseconds(fractionalSeconds)
  );

  const initialOffsetMs = getTimeZoneOffsetMs(new Date(baseUtcMs), timeZone);
  let normalizedUtcMs = baseUtcMs - initialOffsetMs;
  const resolvedOffsetMs = getTimeZoneOffsetMs(new Date(normalizedUtcMs), timeZone);

  if (resolvedOffsetMs !== initialOffsetMs) {
    normalizedUtcMs = baseUtcMs - resolvedOffsetMs;
  }

  return new Date(normalizedUtcMs);
};

export const getTodayInTimeZone = (timeZone?: string, now: Date = new Date()): Date => {
  if (!timeZone) {
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }
  const parts = getDateOnlyFormatter(timeZone).formatToParts(now);
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find(p => p.type === type)?.value ?? '00';
  const year = parseInt(get('year'), 10);
  const month = parseInt(get('month'), 10);
  const day = parseInt(get('day'), 10);
  return new Date(year, month - 1, day);
};

export const getPreviousCalendarDate = (date: Date, timeZone?: string): Date => {
  const parts = new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'numeric', timeZone, year: 'numeric' })
    .formatToParts(date)
    .reduce<Record<string, string>>((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});
  return new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day) - 1));
};

export const buildLateNightLabel = (referenceDate: Date | undefined, lateNightDate: Date, timeZone?: string): string => {
  const formatter = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' });
  const referenceWeekday = referenceDate
    ? formatter.format(referenceDate)
    : new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short' }).format(getPreviousCalendarDate(lateNightDate, timeZone));
  return `Late ${referenceWeekday} Night/${formatter.format(lateNightDate)} Morning`;
};

export const parseDateString = (dateStr: string | null | undefined): Date | undefined => {
  if (!dateStr) return;

  const normalizedDateStr = dateStr.trim();
  const isoDateMatch = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(normalizedDateStr);
  const legacyDateMatch = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(normalizedDateStr);
  const dateParts = isoDateMatch
    ? { day: Number(isoDateMatch[3]), month: Number(isoDateMatch[2]), year: Number(isoDateMatch[1]) }
    : legacyDateMatch
      ? { day: Number(legacyDateMatch[2]), month: Number(legacyDateMatch[1]), year: Number(legacyDateMatch[3]) }
      : undefined;

  if (!dateParts) return;

  const date = new Date(dateParts.year, dateParts.month - 1, dateParts.day);

  if (
    date.getFullYear() !== dateParts.year
    || date.getMonth() !== dateParts.month - 1
    || date.getDate() !== dateParts.day
  ) return;
  return date;
};

export const normalizeDateString = (dateStr: string | null | undefined): string | undefined => {
  const date = parseDateString(dateStr);
  if (!date) return;

  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const toDateLike = (date: Date): Date => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  const dateLike = {
    toISOString: (): string => `${year}-${month}-${day}`,
  };

  return dateLike as unknown as Date;
};

export const toLocalDateFromUTC = (utcDate: Date): Date => {
  return new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate());
};
